<template>
  <div class="home-box">
    <PageTitleWithSearch
      :title="'Club Members (' + totalMembers + ')'"
      :loading="loading"
      :search="search"
      @searchText="searchChange($event)"
    ></PageTitleWithSearch>
    <v-card v-if="!loading && members">
      <v-card-title dark class="pa-2">
        <v-spacer></v-spacer>
        <v-btn
          small
          depressed
          class="white--text"
          color="blue"
          title="Add"
          :to="'/members/create'"
          v-if="canCreateEdit()"
        >
          Create New Member
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>
      <div>
        <v-simple-table v-if="members">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left" width="5%"></th>
                <th class="text-left" width="5%">
                  Member Id
                </th>
                <th class="text-left" width="10%">
                  Display Name
                </th>
                <th class="text-left" width="10%">
                  Club
                </th>
                <th class="text-left" width="8%">
                  Office Roles
                </th>
                <th class="text-left" width="8%">
                  Mobile
                </th>
                <th class="text-center " width="4%">
                  Status
                </th>
                <th width="10%" class="text-center" colspan="3">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in members" :key="item.uuid">
                <td>
                  <v-avatar size="36px"
                    ><img :src="item.profile_image"
                  /></v-avatar>
                </td>
                <td>{{ item.international_id }}</td>
                <td class="text-left">{{ item.display_name }}</td>
                <td class="text-left">{{ item.club.name }}</td>
                <td class="text-left" v-if="item.office_roles">
                  <span v-for="x in item.office_roles" :key="x.uuid"
                    ><v-chip x-small v-if="x.role && x.role.name">{{
                      x.role.name.substring(0, 20)
                    }}</v-chip></span
                  >
                </td>
                <td class="text-left">{{ item.mobile }}</td>
                <td class="text-center" v-if="item.status">
                  <v-chip
                    small
                    class="ma-2"
                    dark
                    :color="getStatusColor(item.status)"
                  >
                    {{ stringFormatting(item.status) }}
                  </v-chip>
                </td>

                <td class="text-center">
                  <v-btn
                    small
                    dark
                    color="green"
                    :to="'/members/info/' + item.uuid"
                    fab
                  >
                    <v-icon dark small>
                      mdi-eye
                    </v-icon>
                  </v-btn>
                </td>
                <td class="text-center">
                  <v-btn
                    v-if="canCreateEdit()"
                    color="orange"
                    :to="'/members/edit/' + item.uuid"
                    fab
                    small
                    dark
                  >
                    <v-icon small> mdi-pencil </v-icon>
                  </v-btn>
                </td>
                <td class="text-center">
                  <v-btn
                    v-if="canDrop()"
                    small
                    dark
                    color="red"
                    @click="dropMemberConfirm(item.uuid)"
                    fab
                  >
                    <v-icon dark small>
                      mdi-delete
                    </v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>
    </v-card>
    <v-card class="pl-5 pr-5">
      <Pagination
        @previous="page--"
        @next="page++"
        :loading="loading"
        :total="total"
        :page="page"
      ></Pagination>
    </v-card>
    <ConfirmModal
      :text="'Do you want to drop this member?'"
      :title="'Drop Member'"
      :color="'red'"
      :confirmOpen="confirmOpen"
      @cancelConfirm="cancelConfirm()"
      @confirm="confirm()"
    ></ConfirmModal>
     <Snackbars :text="successMsg" :snackbar="snackbar" :color="'success'" @classSnackbar="closeSnackbar" />
  </div>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("auth");
import { MemberService } from "../../services/member-service.js";
import { checkError } from "@/config/errors.js";
import PageTitleWithSearch from "@/components/shared/PageTitleWithSearch";
import ConfirmModal from "@/components/shared/ConfirmModal";
import Pagination from "@/components/shared/Pagination";
import Snackbars from "@/components/shared/Snackbars";

export default {
  name: "MemberGrid",
  components: { PageTitleWithSearch, Pagination, ConfirmModal, Snackbars },
  data: function() {
    return {
      members: [],
      page: 1,
      search: "",
      loading: false,
      role: localStorage.getItem("role") || null,
      total: 0,
      totalMembers: 0,
      dropId: null,
      confirmOpen: false,
      snackbar: false,
      successMsg: "Member has been dropped successfully",
    };
  },
  computed: {
    ...mapGetters({
      profile: "profile",
    }),
  },
  mounted() {
    this.$store.dispatch("auth/getProfile");
    this.getMembers();
  },

  watch: {
    search() {
      this.page = 1;
      this.getMembers();
    },
    page() {
      this.getMembers();
    },
  },

  methods: {
    getMembers() {
      this.loading = true;
      MemberService.getClubMembersList(this.search, this.page)
        .then((response) => {
          this.loading = false;
          if (response.data.data) {
            this.members = response.data.data;
            this.total = response.data.meta.last_page || 0;
            this.totalMembers = response.data.meta.total || 0;
          }
        })
        .catch((error) => {
          this.loading = false;
          this.members = [];
          let data = error.response.data;
          let errorOutput = checkError.errorFormatting(data);
          this.errors = errorOutput;
        });
    },
    stringFormatting(str) {
      return str.replace(/_/g, " ");
    },
    getStatusColor(status) {
      let bgColor = "";

      switch (true) {
        case status === "active":
          bgColor = "green";
          break;
        default:
          bgColor = "red";
          break;
      }

      return bgColor;
    },

    searchChange(value) {
      this.search = value;
    },

    canCreateEdit() {
      if (
        this.role === "DISTRICT_GOVERNOR" ||
        this.role === "REGION_CPE_COORDINATOR" ||
        this.role === "CLUBS_PERFORMANCE_EVALUATION" ||
        this.role === "CLUB_ADMINISTRATOR" ||
        this.role === "CLUB_PRESIDENT" || 
        this.role === 'CLUB_SECRETARY' ||
        this.role === "CLUB_TREASURER"
      ) {
        return true;
      } else {
        return false;
      }
    },

    dropMemberConfirm(uuid) {
      this.dropId = uuid;
      this.confirmOpen = true;
    },

    cancelConfirm() {
      this.confirmOpen = false;
    },

    confirm() {
      this.confirmOpen = false;
      this.dropMember();
    },

    dropMember() {
      if (this.dropId) {
        this.loading = true;
        MemberService.dropMember(this.dropId)
          .then((response) => {
            if (response) {
              this.loading = false;
              this.snackbar = true;
              this.getMembers();
            }
          })
          .catch((error) => {
            this.loading = false;
             this.snackbar = false;
            let data = error.response.data;
            let errorOutput = checkError.errorFormatting(data);
            this.errors = errorOutput;
          });
      }
    },

    canDrop() {
      if (
        this.role === "DISTRICT_GOVERNOR" ||
        this.role === "REGION_CPE_COORDINATOR" ||
        this.role === "CLUBS_PERFORMANCE_EVALUATION" ||
        this.role === "CLUB_ADMINISTRATOR" ||
        this.role === "CLUB_PRESIDENT" || 
        this.role === 'CLUB_SECRETARY' ||
        this.role === "CLUB_TREASURER"
      ) {
        return true;
      } else {
        return false;
      }
    },

    closeSnackbar() {
      this.snackbar = false;
    },
  },
};
</script>
